import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { File, FileText, Image as ImageIcon, Loader2, Printer } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_LABEL_SEZIONE = {
  anagrafica: "Anagrafica",
  amministrativa: "Amministrativa",
  sociale: "Sociale",
  sanitaria: "Sanitaria",
  generale: "Generale",
  piani_terapeutici: "Piani terapeutici",
  terapie_ausili: "Terapie e Ausili",
  richiesta_rsa: "Richiesta RSA",
  contratto_firmato: "Contratto firmato",
  legali: "Documenti legali",
  delega_medico_firmata: "Delega medico",
  cambio_medico: "Cambio e rinnovo medico",
};

const SEZIONI_ORDINE = [
  "contratto_firmato",
  "anagrafica",
  "amministrativa",
  "sociale",
  "sanitaria",
  "legali",
  "delega_medico_firmata",
  "terapie_ausili",
  "piani_terapeutici",
  "richiesta_rsa",
  "generale",
];

function docKey(doc) {
  return doc.file_id;
}

function isImageDoc(doc) {
  if (doc.file_type?.startsWith("image/")) return true;
  const ext = (doc.file_ext || "").toLowerCase();
  return [".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(ext);
}

function isPdfDoc(doc) {
  if (doc.file_type === "application/pdf") return true;
  return (doc.file_ext || "").toLowerCase() === ".pdf";
}

function DocIcon({ doc }) {
  if (isImageDoc(doc)) return <ImageIcon className="h-4 w-4 text-blue-600 shrink-0" />;
  if (isPdfDoc(doc)) return <FileText className="h-4 w-4 text-red-600 shrink-0" />;
  return <File className="h-4 w-4 text-slate-600 shrink-0" />;
}

function parseSkippedHeader(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function extractErrorDetail(error) {
  const data = error?.response?.data;
  if (!data) return null;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.detail || null;
    } catch {
      return null;
    }
  }
  if (typeof data === "object" && data.detail) return data.detail;
  return null;
}

export default function StampaDocumentiFascicoloDialog({
  open,
  onOpenChange,
  fascicoloId,
  token,
  documenti = [],
  labelSezione = DEFAULT_LABEL_SEZIONE,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [printing, setPrinting] = useState(false);

  const groupedDocs = useMemo(() => {
    const groups = {};
    (documenti || []).forEach((doc) => {
      if (!docKey(doc)) return;
      const sezione = doc.sezione || "generale";
      if (!groups[sezione]) groups[sezione] = [];
      groups[sezione].push(doc);
    });
    const ordered = SEZIONI_ORDINE
      .filter((sezione) => groups[sezione]?.length)
      .map((sezione) => ({
        sezione,
        label: labelSezione[sezione] || sezione,
        docs: groups[sezione],
      }));
    const extras = Object.keys(groups)
      .filter((sezione) => !SEZIONI_ORDINE.includes(sezione))
      .sort()
      .map((sezione) => ({
        sezione,
        label: labelSezione[sezione] || sezione,
        docs: groups[sezione],
      }));
    return [...ordered, ...extras];
  }, [documenti, labelSezione]);

  const allIds = useMemo(() => {
    const seen = new Set();
    const ids = [];
    (documenti || []).forEach((doc) => {
      const id = docKey(doc);
      if (!id || seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
    return ids;
  }, [documenti]);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(allIds);
    setPrinting(false);
  }, [open, allIds]);

  const toggleDoc = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSection = (sectionDocs, checked) => {
    const ids = sectionDocs.map(docKey);
    setSelectedIds((prev) => {
      if (checked) return [...new Set([...prev, ...ids])];
      return prev.filter((id) => !ids.includes(id));
    });
  };

  const setAll = (checked) => setSelectedIds(checked ? allIds : []);

  const selectedDocs = useMemo(() => {
    const seen = new Set();
    return (documenti || []).filter((doc) => {
      const id = docKey(doc);
      if (!id || !selectedIds.includes(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [documenti, selectedIds]);

  const handlePrint = async () => {
    if (!fascicoloId || !token) {
      toast.error("Sessione non valida. Effettua di nuovo l'accesso.");
      return;
    }
    if (selectedDocs.length === 0) {
      toast.error("Seleziona almeno un documento da stampare");
      return;
    }

    const printWindow = window.open("about:blank", "_blank");
    if (!printWindow) {
      toast.error("Popup bloccato dal browser. Abilita i popup per questo sito e riprova.");
      return;
    }

    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Preparazione stampa documenti</title>
  <style>
    body { font-family: system-ui, sans-serif; padding: 2rem; color: #334155; }
  </style>
</head>
<body>
  <p>Preparazione documenti per la stampa...</p>
</body>
</html>`);
    printWindow.document.close();

    setPrinting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/documenti/stampa`,
        { file_ids: selectedDocs.map(docKey) },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const skipped = parseSkippedHeader(response.headers["x-affidya-stampe-skipped"]);
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      printWindow.location.href = blobUrl;
      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch {
          /* Il viewer PDF gestisce la stampa manualmente se necessario */
        }
      }, 900);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 120000);

      const mergedCount = selectedDocs.length - skipped.length;
      if (skipped.length > 0) {
        toast.warning(
          `PDF unificato creato (${mergedCount} documenti). Esclusi dalla stampa: ${skipped.join(", ")}`
        );
      } else {
        toast.success(
          `PDF unificato pronto per la stampa (${mergedCount} ${mergedCount === 1 ? "documento" : "documenti"})`
        );
      }

      onOpenChange(false);
    } catch (error) {
      console.error("Errore stampa documenti fascicolo:", error);
      printWindow.close();
      const detail = await extractErrorDetail(error);
      toast.error(detail || "Errore nella preparazione del PDF di stampa");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5 text-blue-600" />
            Stampa documenti fascicolo
          </DialogTitle>
          <DialogDescription>
            Seleziona i documenti da stampare. Verrà generato un unico PDF temporaneo con tutti i file selezionati, pronto per la stampa.
          </DialogDescription>
        </DialogHeader>

        {documenti.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">
            Nessun documento allegato al fascicolo.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-3">
              <p className="text-sm text-slate-600">
                {selectedIds.length} di {allIds.length} documenti selezionati
              </p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setAll(true)}>
                  Seleziona tutti
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setAll(false)}>
                  Deseleziona tutti
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-5 py-2 pr-1">
              {groupedDocs.map(({ sezione, label, docs }) => {
                const sectionIds = docs.map(docKey);
                const allSectionSelected = sectionIds.every((id) => selectedIds.includes(id));
                const someSectionSelected = sectionIds.some((id) => selectedIds.includes(id));

                return (
                  <div key={sezione}>
                    <div className="mb-2 flex items-center gap-2">
                      <Checkbox
                        id={`stampa-sezione-${sezione}`}
                        checked={allSectionSelected ? true : someSectionSelected ? "indeterminate" : false}
                        onCheckedChange={(checked) => toggleSection(docs, checked === true)}
                      />
                      <label
                        htmlFor={`stampa-sezione-${sezione}`}
                        className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-800"
                      >
                        <Badge variant="outline">{label}</Badge>
                        <span className="text-xs font-normal text-slate-400">({docs.length})</span>
                      </label>
                    </div>

                    <div className="space-y-2 pl-6">
                      {docs.map((doc) => {
                        const id = docKey(doc);
                        return (
                          <label
                            key={id}
                            htmlFor={`stampa-doc-${id}`}
                            className="flex cursor-pointer items-start gap-3 rounded-lg border bg-slate-50 p-3 hover:bg-slate-100"
                          >
                            <Checkbox
                              id={`stampa-doc-${id}`}
                              checked={selectedIds.includes(id)}
                              onCheckedChange={() => toggleDoc(id)}
                              className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <DocIcon doc={doc} />
                                <p className="truncate text-sm font-medium text-slate-900">{doc.file_name}</p>
                              </div>
                              {doc.descrizione ? (
                                <p className="mt-0.5 truncate text-xs text-slate-500">{doc.descrizione}</p>
                              ) : null}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter className="border-t pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={printing}>
            Annulla
          </Button>
          <Button
            type="button"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handlePrint}
            disabled={printing || selectedIds.length === 0 || documenti.length === 0}
          >
            {printing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Printer className="mr-2 h-4 w-4" />
            )}
            {printing ? "Preparazione PDF..." : `Stampa selezionati (${selectedIds.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
